import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import loadCurrentDocumentMarkdown from './loadCurrentDocumentMarkdown';
import useDocumentStorageService from 'domain/shared/documentStorage/documentStorageService';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { hasMarkdownExtension, isMarkdownFile } from '../shared/markdownUtils';

const useComplianceDocumentVersionEditorData = () => {
  const [content, setContent] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [hasUploadedNewDocument, setHasUploadedNewDocument] = useState(false);
  const loadedPathRef = useRef<string | null>(null);
  const shownReadonlyMessagePathRef = useRef<string | null>(null);
  const { search } = useLocation();
  const { getDocumentToken } = useDocumentStorageContext();
  const { uploadArtifact } = useDocumentStorageService();
  const { addMessage } = useMessageService();

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const sourceDocumentPath = params.get('sourceDocumentPath') || '';
  const currentDocumentPath = params.get('currentDocumentPath') || '';
  const documentPathToEdit = sourceDocumentPath || currentDocumentPath;
  const isMarkdownPath = hasMarkdownExtension(documentPathToEdit);

  useEffect(() => {
    const loadCurrentDocument = async () => {
      if (loadedPathRef.current === documentPathToEdit) return;

      if (!isMarkdownPath) {
        setContent('');
        setInitialContent('');
        setHasUploadedNewDocument(false);
        loadedPathRef.current = documentPathToEdit;

        if (shownReadonlyMessagePathRef.current !== documentPathToEdit) {
          addMessage(new ErrorMessage('Only .md documents can be edited. PDF is preview-only.'));
          shownReadonlyMessagePathRef.current = documentPathToEdit;
        }
        return;
      }

      try {
        const { content: currentContent } = await loadCurrentDocumentMarkdown({
          currentDocumentPath: documentPathToEdit,
          getDocumentToken,
        });
        setContent(currentContent);
        setInitialContent(currentContent);
        setHasUploadedNewDocument(false);
        loadedPathRef.current = documentPathToEdit;
      } catch {
        addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
      }
    };

    void loadCurrentDocument();
  }, [addMessage, documentPathToEdit, getDocumentToken, isMarkdownPath]);

  const uploadAndLoadMarkdown = async (file: File) => {
    if (!isMarkdownFile(file)) {
      addMessage(new ErrorMessage('Only .md documents are allowed. PDF upload is not supported.'));
      return;
    }

    try {
      const uploadedPath = await uploadArtifact({ file });

      const { content: uploadedContent } = await loadCurrentDocumentMarkdown({
        currentDocumentPath: uploadedPath,
        getDocumentToken,
      });

      setContent(uploadedContent);
      setInitialContent(uploadedContent);
      setHasUploadedNewDocument(true);
      loadedPathRef.current = documentPathToEdit;
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const hasChanges = hasUploadedNewDocument || content !== initialContent;

  return {
    content,
    setContent,
    uploadAndLoadMarkdown,
    hasChanges,
    isMarkdownPath,
    search,
    documentPathToEdit,
  };
};

export default useComplianceDocumentVersionEditorData;
